import * as Sentry from '@sentry/react';
import { createEffect, sample } from 'effector';
import { t } from 'i18n';
import { keyBy } from 'lodash';
import $$api from 'models/api';
import $$auth from 'models/auth';
import { $global } from 'models/store-global';

import AlertsService from 'services/AlertsService';
import AuthService from 'services/AuthService';

import { MyStore } from 'types/Store';
import { CurrentParticipant, CurrentParticipantZod } from 'types/zod/current-participant';
import { type Participant, ParticipantZod } from 'types/zod/participant';

sample({
    clock: [
        $$api.participants.createParticipantFx.failData,
        $$api.participants.deleteObserverFx.failData,
        $$api.participants.findParticipantFx.failData,
        $$api.participants.getAdvisorByIdFx.failData,
        $$api.participants.getAdvisorsFx.failData,
        $$api.participants.getCommunityFx.failData,
        $$api.participants.getFolderPathFx.failData,
        $$api.participants.getMeFx.failData,
        $$api.participants.getParticipantFx.failData,
        $$api.participants.getAdvisorParticipantFx.failData,
        $$api.participants.getParticipantsFx.failData,
        $$api.participants.getTempUserFx.failData,
        $$api.participants.isDirectInvoicingFx.failData,
        $$api.participants.linkRegisteredUserFx.failData,
        $$api.participants.lookupAddressesFx.failData,
        $$api.participants.resendNotificationsFx.failData,
        $$api.participants.sendInvitationFx.failData,
        $$api.participants.setMailingAddressFx.failData,
        $$api.participants.setNewUserEmailFx.failData,
        $$api.participants.updateEmailFx.failData,
        $$api.participants.updateParticipantFx.failData,
        $$api.participants.updateParticipantsFx.failData,
        $$api.participants.verifyLinkingFx.failData,
        $$api.participants.updateUserSettingsFx.failData,
        $$api.participants.getParticipantReferencesFx.failData,
        $$api.participants.removeParticipantFx.failData,
    ],
    target: createEffect(AlertsService.handleError),
});

// Handle $$api.participants._createParticipant.doneData
sample({
    clock: $$api.participants._createParticipantFx.doneData,
    fn: () => t('newDashboardPage.participantSuccessUpdate'),
    target: createEffect(AlertsService.showSuccess),
});

// Handle $$api.participants.getAdvisorById.doneData
sample({
    source: $global,
    clock: $$api.participants.getAdvisorByIdFx.doneData,
    fn: (store, data): MyStore => ({
        ...store,
        participants: {
            ...(store.participants || {}),
            [data.participant_id]: data,
        },
    }),
    target: $global,
});

// Handle $$api.participants.getAdvisors.doneData
sample({
    source: $global,
    clock: $$api.participants.getAdvisorsFx.doneData.map((data) => keyBy(data, 'participant_id')),
    fn: (global, participants): MyStore => ({
        ...global,
        participants: { ...(global?.participants || {}), ...participants },
    }),
    target: $global,
});

// Handle $$api.participants.getMe.doneData
sample({
    clock: $$api.participants.getMeFx.doneData.map(AuthService.removePreliminaryFields),
    target: $$auth.$currentParticipant,
});
sample({
    source: $global,
    clock: $$api.participants.getMeFx.doneData.map(AuthService.getIsPreliminaryUser),
    filter: (_store, isPreliminaryUser) => isPreliminaryUser,
    fn: (store) => ({
        ...store,
        isPreliminaryUser: true,
    }),
    target: $global,
});
sample({
    source: $global,
    clock: $$api.participants.getMeFx.doneData.map(AuthService.getIsPreliminaryUser),
    filter: (isPreliminaryUser) => !isPreliminaryUser,
    fn: ({ isPreliminaryUser, ...global }) => global,
    target: $global,
});
sample({
    clock: $$api.participants.getMeFx.doneData,
    target: createEffect(async (data: CurrentParticipant) => {
        const userId = '' + data.user.user_id;
        if (window.FS) {
            FS.identify(userId, { displayName: data.full_name, email: data.email });
        }
        if (window.heap) {
            heap.identify(userId);
            heap.addUserProperties({ name: data.full_name, email: data.email });
        }
        if (window.hj) {
            hj('identify', userId, { name: data.full_name, email: data.email });
        }
        if (window._hsq) {
            _hsq.push([
                'identify',
                {
                    email: data.email,
                },
            ]);
        }

        if (window.Upscope) {
            Upscope('updateConnection', {
                identities: [data.full_name, data.email],
                uniqueId: userId,
            });
        }

        Sentry.setUser({ id: data.user.aws_id, username: data.full_name, email: data.email });
    }),
});

// Handle $$api.participants.getParticipant.doneData
sample({
    source: $global,
    clock: $$api.participants.getParticipantFx.doneData,
    fn: (store, data): MyStore => ({
        ...store,
        participants: {
            ...(store.participants || {}),
            [data.participant_id]: data,
        },
    }),
    target: $global,
});

// Handle $$api.participants.linkRegisteredUser.doneData
sample({
    source: $$api.participants.linkRegisteredUserFx.doneData,
    filter: ({ status }) => status === 200,
    target: createEffect(async () => {
        await $$api.participants.getMeFx();
        AlertsService.showSuccess(t('participantsLinking.success'));
    }),
});

sample({
    source: $$api.participants.linkRegisteredUserFx.doneData,
    filter: ({ status }) => status === 204,
    fn: ({ data }) => ParticipantZod.parse(data),
    target: createEffect(async ({ email }: Participant) => {
        AlertsService.showWarning(t('participantsLinking.needToVerify', { recipient: email }));
    }),
});

// Handle $$api.participants.sendInvitation.doneData
sample({
    clock: $$api.participants.sendInvitationFx.doneData,
    fn: () => t('newDashboardPage.referalTabSuccessMessage'),
    target: createEffect(AlertsService.showSuccess),
});

// Handle $$api.participants.updateParticipant.doneData
sample({
    source: $$auth.$currentParticipant.map((p) => p?.participant_id),
    clock: $$api.participants.updateParticipantFx.doneData,
    filter: (currentParticipantId, participant) =>
        participant.participant_id === currentParticipantId && !!participant.user,
    fn: (_currentParticipantId, participant) =>
        AuthService.removePreliminaryFields(CurrentParticipantZod.parse(participant)),
    target: $$auth.$currentParticipant,
});

// If current participant was updated, show success alert
sample({
    source: $$auth.$currentParticipant.map((p) => p?.participant_id),
    clock: $$api.participants.updateParticipantFx.doneData,
    filter: (currentParticipantId, participant) => participant.participant_id === currentParticipantId,
    fn: () => t('newDashboardPage.participantSuccessUpdate'),
    target: createEffect(AlertsService.showSuccess),
});

// Handle $$api.participants.updateParticipants.doneData
sample({
    source: $$auth.$currentParticipant,
    clock: $$api.participants.updateParticipantsFx.doneData,
    // fn: AuthService.removePreliminaryFields,
    filter: Boolean,
    fn: (currentParticipant, updatedParticipant) => {
        const newCurrentParticipant = (updatedParticipant.successfully || []).find(
            (p: Participant) => p.participant_id === currentParticipant?.participant_id,
        ) as CurrentParticipant | undefined;
        return AuthService.removePreliminaryFields(newCurrentParticipant || currentParticipant);
    },
    target: $$auth.$currentParticipant,
});

// Handle $$api.participants.verifyLinking.doneData
sample({
    clock: $$api.participants.verifyLinkingFx.doneData,
    filter: ({ status }) => status === 200,
    target: createEffect(async () => {
        await $$api.participants.getMeFx();
        AlertsService.showSuccess(t('participantsLinking.success'));
    }),
});
sample({
    clock: $$api.participants.verifyLinkingFx.doneData,
    filter: ({ status }) => status === 204,
    fn: () => t('participantsLinking.badVerificationAttempt'),
    target: createEffect(AlertsService.showWarning),
});

// Handle findParticipant.doneData
sample({
    source: $global,
    clock: $$api.participants.findParticipantFx.doneData.map((data) => keyBy(data, 'participant_id')),
    fn: (global, participants): MyStore => ({
        ...global,
        participants: { ...(global?.participants || {}), ...participants },
    }),
    target: $global,
});

// Handle getCommunity success
sample({
    source: $global,
    clock: $$api.participants.getCommunityFx.done.map(({ result: community, params: participantId }) => ({
        community,
        participantId,
    })),
    fn: (store, { community, participantId }): MyStore => {
        if (participantId) {
            return {
                ...store,
                advisorClient: {
                    ...(store.advisorClient || {}),
                    clients: {
                        ...(store.advisorClient?.clients || {}),
                        [participantId]: {
                            ...(store.advisorClient?.clients?.[participantId] || {}),
                            community: community,
                        },
                    },
                },
            };
        }
        return {
            ...store,
            community: community,
        };
    },
    target: $global,
});
